import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId

const environment = 'snitch'

export default {
  namespaced: true,
  state: {
    serviceUnavailable: false,
    serviceRequested: false,
    fetchingHosts: false,
    hosts: [],
    host: {},
    hostsResultInfo: {},
    currentPage: 1,
    removingHost: false,
    gettingHost: false,
    addingHost: false,
    gettingHostMemory: false,
    hostMemory: null,
    gettingHostCpu: false,
    hostCpu: [],
    gettingHostNetwork: false,
    hostNetworkSend: null,
    hostNetworkReceive: null,
    fetchingAgents: false,
    agents: [],
    initiatingMetrics: false,
    networkInterfaces: [],
    gettingNetworkInterfaces: false,
    networkInterface: null,
    gettingFileSystem: false,
    fileSystem: [],
  },
  getters: {
    currentPage: state => state.currentPage,
  },
  mutations: {
    SET_SERVICE_UNAVAILABLE(state, { serviceUnavailable, serviceRequested }) {
      state.serviceUnavailable = serviceUnavailable
      state.serviceRequested = serviceRequested
    },
    SET_FETCHING_HOSTS(state, value) {
      state.fetchingHosts = value
    },
    SET_HOSTS(state, value) {
      state.hosts = value.result
      state.hostsResultInfo = value.resultInfo
    },
    GET_HOST(state, value) {
      state.host = value
    },
    SET_ADDING_HOST(state, value) {
      state.addingHost = value
    },
    SET_REMOVING_HOST(state, value) {
      state.removingHost = value
    },
    REMOVE_HOST(state, id) {
      state.hosts = state.hosts.filter(h => h.id !== id)
    },
    SET_GETTING_HOST(state, value) {
      state.gettingHost = value
    },
    SET_GETTING_HOST_MEMORY(state, value) {
      state.gettingHostMemory = value
    },
    GET_HOST_MEMORY(state, value) {
      state.hostMemory = value
    },
    SET_GETTING_HOST_CPU(state, value) {
      state.gettingHostCpu = value
    },
    GET_HOST_CPU(state, value) {
      state.hostCpu = value
    },
    SET_GETTING_HOST_NETWORK(state, value) {
      state.gettingHostNetwork = value
    },
    GET_HOST_NETWORK_RECEIVE(state, value) {
      state.hostNetworkReceive = value
    },
    GET_HOST_NETWORK_SEND(state, value) {
      state.hostNetworkSend = value
    },
    SET_FETCHING_AGENTS(state, value) {
      state.fetchingAgents = value
    },
    SET_AGENTS(state, value) {
      state.agents = value
    },
    SET_INITIATING_METRICS(state, value) {
      state.initiatingMetrics = value
    },
    SET_NETWORK_INTERFACES(state, options) {
      state.networkInterfaces = options
    },
    SET_GETTING_NETWORK_INTERFACES(state, value) {
      state.gettingNetworkInterfaces = value
    },
    SET_NETWORK_INTERFACE(state, value) {
      state.networkInterface = value
    },
    SET_GETTING_FILE_SYSTEM(state, value) {
      state.gettingFileSystem = value
    },
    SET_FILE_SYSTEM(state, value) {
      state.fileSystem = value
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
  },
  actions: {
    fetchHosts({ commit }, query) {
      commit('SET_FETCHING_HOSTS', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(
            `projects/${getProjectId()}/hosts?PageIndex=${
              query.pageIndex
            }&PageSize=${query.pageSize}`,
          )
          .then(response => {
            commit('SET_HOSTS', response.data)
            resolve(response)
          })
          .catch(error => {
            if (
              error.response?.status === 426
              || error.response?.status === 428
            ) {
              commit('SET_SERVICE_UNAVAILABLE', error.response?.status === 428)
            }
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_HOSTS', false)
          })
      })
    },

    addHost({ commit }, query) {
      commit('SET_ADDING_HOST', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .post(`/projects/${getProjectId()}/hosts`, query)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_ADDING_HOST', false)
          })
      })
    },

    getHost({ commit }, id) {
      commit('SET_GETTING_HOST', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`/projects/${getProjectId()}/hosts/${id}`)
          .then(response => {
            commit('GET_HOST', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_HOST', false)
          })
      })
    },

    removeHost({ commit }, id) {
      commit('SET_REMOVING_HOST', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .delete(`/projects/${getProjectId()}/hosts/${id}`)
          .then(response => {
            commit('REMOVE_HOST', id)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_REMOVING_HOST', false)
          })
      })
    },

    getHostMemory({ commit }, id) {
      commit('SET_GETTING_HOST_MEMORY', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`/projects/${getProjectId()}/hosts/${id}/memory`)
          .then(response => {
            commit('GET_HOST_MEMORY', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_HOST_MEMORY', false)
          })
      })
    },

    getHostNetwork({ commit }, query) {
      commit('SET_GETTING_HOST_NETWORK', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(
            `/projects/${getProjectId()}/hosts/${query.id}/network?type=${
              query.type
            }&deviceId=${query.deviceId}`,
          )
          .then(response => {
            commit(
              `GET_HOST_NETWORK_${query.type.toUpperCase()}`,
              response.data.result,
            )
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_HOST_NETWORK', false)
          })
      })
    },

    getHostCPU({ commit }, id) {
      commit('SET_GETTING_HOST_CPU', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`/projects/${getProjectId()}/hosts/${id}/cpu`)
          .then(response => {
            commit('GET_HOST_CPU', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_HOST_CPU', false)
          })
      })
    },

    fetchAgents({ commit }, platform) {
      const query = platform ? `?platform=${platform}` : ''
      commit('SET_FETCHING_AGENTS', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`projects/${getProjectId()}/agents${query}`)
          .then(response => {
            commit('SET_AGENTS', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_AGENTS', false)
          })
      })
    },

    initMetrics({ commit }) {
      commit('SET_INITIATING_METRICS', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .post(`/projects/${getProjectId()}/init`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_INITIATING_METRICS', false)
          })
      })
    },

    getNetworkInterfaces({ commit }, id) {
      commit('SET_GETTING_NETWORK_INTERFACES', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`/projects/${getProjectId()}/hosts/${id}/network-interfaces`)
          .then(response => {
            commit('SET_NETWORK_INTERFACES', response.data.result)
            commit(
              'SET_NETWORK_INTERFACE',
              response.data.result.length > 0 ? response.data.result[0] : '',
            )
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_NETWORK_INTERFACES', false)
          })
      })
    },

    getFileSystem({ commit }, id) {
      commit('SET_GETTING_FILE_SYSTEM', true)
      return new Promise((resolve, reject) => {
        api(environment)
          .get(`/projects/${getProjectId()}/hosts/${id}/filesystem`)
          .then(response => {
            commit('SET_FILE_SYSTEM', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_FILE_SYSTEM', false)
          })
      })
    },
  },
}
